import { useMemo } from "react"

import { useQuery } from "@tanstack/react-query"
import { parseISO } from "date-fns"
import { TConversation, TMessage } from "types/transcript"

import {
  useCurrentConversation,
  usePastConversations,
  useSynchronizeAllConversationsFromQueryStatus,
} from "src/queries/useConversation"
import { computeMessageKey } from "src/utils/messageUtils"

// Helper function to check for duplicate messages in a transcript
const deduplicateMessages = (messages: TMessage[], errorText: string): TMessage[] => {
  const messageKeys = new Set<string>()
  const duplicateKeys = new Set<string>()
  const uniqueMessages: TMessage[] = []

  messages.forEach((message) => {
    const messageKey = computeMessageKey(message)

    if (!messageKey) {
      // No dedup key (e.g. PlayVideo messages without a server-assigned message_id due to
      // C2-2210) — cannot deduplicate, always keep.
      uniqueMessages.push(message)
      return
    }

    if (messageKeys.has(messageKey)) {
      duplicateKeys.add(messageKey)
    } else {
      messageKeys.add(messageKey)
      uniqueMessages.push(message)
    }
  })

  const duplicateKeysArray = Array.from(duplicateKeys)

  if (duplicateKeysArray.length > 0) {
    console.error(errorText, duplicateKeysArray)
  }

  return uniqueMessages
}

// Copy properties from the parent conversation to a child message
const addMessageProperties = (msg: TMessage, conversation: TConversation) => {
  return {
    ...msg,
    conversation_id: conversation?.id,
    active_esk_sponsor: conversation?.active_esk_sponsor,
    active_esk_type: conversation?.active_esk_type,
    sidekick_name: conversation?.sidekick_name,
  }
}

// this hook is a helper for our SidekickChat list
// it gathers the messages from the current conversation and the past conversations
// and does any filtering / deduplicating / sorting needed prior to display
export const useChatMessageList = () => {
  const currentConversationQuery = useCurrentConversation()
  const currentConversation = currentConversationQuery.data
  const { pastConversations } = usePastConversations()
  const { isSynchronizing } = useSynchronizeAllConversationsFromQueryStatus()

  // Extended conversations loaded on-demand when the user scrolls to the top
  // of the chat (beyond the initial page of history). These are held in the
  // React Query in-memory cache only — never persisted to localStorage — so
  // they never contribute to the web localStorage quota.
  const { data: extendedPastConversations = [] } = useQuery<TConversation[]>({
    queryKey: ["conversations", "extended-past"],
    queryFn: () => [],
    staleTime: Infinity,
    gcTime: Infinity,
  })

  const filteredMessages = useMemo(() => {
    const orderTranscript = (transcript: TMessage[]) => {
      // TODO: we're filtering out event-only messages right now, may need to show them if we want to display thumbnails
      return transcript
        .filter((item) => item.text || item.events?.length)
        .sort((a, b) => {
          const tA = a?.created_at && parseISO(a.created_at.toString())
          const tB = b?.created_at && parseISO(b.created_at.toString())
          if (!tA || !tB) return 0
          return tB.getTime() - tA.getTime()
        })
    }

    const allPastConversations = [...pastConversations, ...extendedPastConversations]
    const nonEmptyPastConversations = allPastConversations.filter(
      (conversation) => (conversation?.transcript?.length || 0) > 0,
    )

    // Combined list of all processed messages
    let messages: TMessage[] = []

    const currentConvoMessages = currentConversation
      ? orderTranscript(currentConversation.transcript || []).map((message) =>
          addMessageProperties(message, currentConversation),
        )
      : []

    // Check for duplicates in current conversation messages
    const dedupedCurrentMessages = deduplicateMessages(
      currentConvoMessages,
      "useChatMessageList: Found duplicate messages within current conversation",
    )

    // Add current conversation messages to the combined list
    messages = messages.concat(dedupedCurrentMessages)

    // Check for duplicates in past conversations
    const pastConvoMessages =
      nonEmptyPastConversations?.flatMap((pastConvo) =>
        orderTranscript(pastConvo?.transcript || []).map((message) =>
          addMessageProperties(message, pastConvo),
        ),
      ) || []

    // Check for duplicates in past conversations
    const dedupedPastMessages = deduplicateMessages(
      pastConvoMessages,
      "useChatMessageList: Found duplicate messages within past conversations",
    )

    // Add past conversation messages to the combined list
    messages = messages.concat(dedupedPastMessages)

    // Check for duplicates in final combined list and remove them
    const deduplicatedMessages = deduplicateMessages(
      messages,
      "useChatMessageList: Found duplicate messages within all conversations",
    )

    // One final sort to make sure that all messages are in the correct order
    return deduplicatedMessages.sort((a, b) => {
      const tA = a?.created_at && parseISO(a.created_at.toString())
      const tB = b?.created_at && parseISO(b.created_at.toString())
      if (!tA || !tB) return 0
      return tB.getTime() - tA.getTime()
    })
  }, [pastConversations, extendedPastConversations, currentConversation])

  return {
    messages: filteredMessages,
    isLoading: isSynchronizing,
    isPending: currentConversationQuery.isPending,
  }
}
